import { useMemo } from 'react';

import { Text, Flex, Grid, Badge, Spacer, Skeleton } from '@backyard-ui/core';

import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { type Shipment } from '@/infra/services/catalog/product/get-shipments/model';
import { BuyButtons } from '@/presenters/components/catalog/product/BuyBox/BuyButtons';
import { Image } from '@/presenters/components/common/Image';
import { formatPrice } from '@/utils/price/formatPrice';

interface SellerItemProps {
  seller: Seller;
  shipment: {
    isFetching: boolean;
    isFetched: boolean;
    data?: Shipment;
  };
  onAddToCart: () => void;
}

function SellerItem(props: Readonly<SellerItemProps>) {
  const {
    seller,
    shipment: {
      data: shipment,
      isFetched: shipmentIsFetched,
      isFetching: shipmentIsFetching,
    },
    onAddToCart,
  } = props;

  const {
    shop: { logo, name },
    pricing: { price, installment },
  } = seller;

  const shipmentContent = useMemo(() => {
    const chepeastModality = shipment?.chepeastModality;
    const hasShipmentError = shipmentIsFetched && !chepeastModality;
    const isFreeShipping = chepeastModality?.cost === 0;

    const shipmentText = chepeastModality
      ? `${formatPrice(chepeastModality.cost)} de frete`
      : 'frete';

    if (shipmentIsFetching) {
      return (
        <>
          <Spacer size="2" axis="vertical" />
          <Skeleton height="16px" width="40px" />
        </>
      );
    }

    if (hasShipmentError) {
      return <Text color="red-600">Confira o valor de frete no carrinho</Text>;
    }

    if (isFreeShipping) {
      return (
        <>
          <Spacer size="2" axis="vertical" />
          <Badge
            appearance="critical"
            UNSAFE_className="self-start whitespace-nowrap"
          >
            <span>Frete grátis</span>
          </Badge>
        </>
      );
    }

    return <Text>+ {shipmentText}</Text>;
  }, [shipment?.chepeastModality, shipmentIsFetching, shipmentIsFetched]);

  return (
    <Grid.Root className="w-full" columns={7}>
      <Grid.Item size={{ md: 1 }}>
        <Image
          src={logo}
          alt={`Logo do seller ${name}`}
          className="h-14 w-14"
          width={56}
          height={56}
        />
      </Grid.Item>
      <Grid.Item>
        <Flex direction="column" className="py-4 md:py-0">
          <Text>
            <strong>{formatPrice(price.to)}</strong> à vista
          </Text>
          <Text>
            {installment.quantity}x de {formatPrice(installment.value)}
          </Text>
          <Text>{installment.brandedQuantity}x no cartão celebre</Text>
          {shipmentContent}
        </Flex>
      </Grid.Item>
      <Grid.Item size={{ md: 3 }}>
        <BuyButtons
          seller={seller}
          direction="column"
          onSuccess={onAddToCart}
        />
      </Grid.Item>
    </Grid.Root>
  );
}

export default SellerItem;
